import React, { useCallback, useState, type JSX, useRef, useEffect } from 'react';
import { 
  FaPlus, FaTimes, FaImage, FaUtensils, 
  FaStore, FaFileAlt, FaGripVertical,
  FaCloudUploadAlt, FaCheck, FaRedo
} from 'react-icons/fa';
import type { FoodItem, FormFoodItem, FormGalleryItem, GalleryItem, RestaurantFormData } from '../restaurant.types';
import { uploadFile } from '../../../lib/api/restaurant.apis';

interface GalleryStepProps {
  formData: RestaurantFormData;
  updateFormData: (data: Partial<RestaurantFormData>) => void;
  setDisableNext?: (disabled: boolean) => void;
  isActive: boolean;
}

const GalleryStep: React.FC<GalleryStepProps> = ({
  formData,
  updateFormData,
  isActive,
  setDisableNext
}) => {
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [uploadingItems, setUploadingItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    const hasUploading = uploadingItems.size > 0;
    if (hasUploading) {
      setDisableNext && setDisableNext(true);
    }
  }, [uploadingItems]);
  
  // Use a ref to track the latest formData to avoid stale closures
  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const CircularProgressBar = () => {
    return (
      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-1"></div>
          <p className="text-xs">Uploading...</p>
        </div>
      </div>
    );
  };

  const handleUploadItem = async (
    item: FormGalleryItem | FoodItem,
    category: 'cover' | 'menu' | 'food' | 'ambiance'
  ) => {
    const itemId = item.id;
    
    try {
      setUploadingItems(prev => new Set(prev).add(itemId));

      const result = await uploadFile(item.url as File);

      // Use the ref to get the latest formData to avoid stale closure
      const currentFormData = formDataRef.current;
      
      // Update the specific item in the parent state
      if (category === 'food' || category === 'ambiance' || category === "menu") {
        const currentItems = currentFormData.gallery[category] || [];
        
        const updatedItems = currentItems.map(i => 
          i.id === itemId 
            ? { 
                ...i, 
                status: 'uploaded', 
                url: result.url, 
                publicId: result.publicId, 
                progress: 100 
              }
            : i
        );
                
        updateFormData({
          gallery: {
            ...currentFormData.gallery,
            [category]: updatedItems
          }
        });
      } else {
        // For cover 
        updateFormData({
          gallery: {
            ...currentFormData.gallery,
            [category]: { 
              ...item, 
              status: 'uploaded', 
              url: result.url, 
              publicId: result.publicId, 
              progress: 100 
            }
          }
        });
      }

    } catch (error) {      
      const currentFormData = formDataRef.current;
      
      if (category === 'food' || category === 'ambiance' || category === "menu") {
        const currentItems = currentFormData.gallery[category] || [];
        const updatedItems = currentItems.map(i => 
          i.id === itemId 
            ? { ...i, status: 'error', progress: 0 }
            : i
        );
        
        updateFormData({
          gallery: {
            ...currentFormData.gallery,
            [category]: updatedItems
          }
        });
      } else {
        updateFormData({
          gallery: {
            ...currentFormData.gallery,
            [category]: { ...item, status: 'error', progress: 0 }
          }
        });
      }
    } finally {
      setUploadingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  const retryUpload = (item: GalleryItem | FoodItem, category: 'cover' | 'menu' | 'food' | 'ambiance') => {
    handleUploadItem(item, category);
  };

  const handleFileUpload = useCallback((
    category: 'cover' | 'food' | 'ambiance' | 'menu',
    files: FileList,
    multiple: boolean = false
  ) => {
    const fileArray = Array.from(files);

    if (category === 'food') {
      const newFoodImages: FormFoodItem[] = fileArray.map(file => ({
        url: file,
        label: '',
        id: Math.random().toString(36).substr(2, 9),
        status: 'uploading',
        progress: 0,
      }));

      const currentFood = formData.gallery.food || [];
      const updatedFood = [...currentFood, ...newFoodImages];
      
      // Update parent state first
      updateFormData({
        gallery: {
          ...formData.gallery,
          food: updatedFood
        }
      });

      // Then start uploads - use setTimeout to ensure state update happens first
      setTimeout(() => {
        newFoodImages.forEach(item => handleUploadItem(item, 'food'));
      }, 0);

    } else if (category === 'ambiance') {
      const newItems: FormGalleryItem[] = fileArray.map(file => ({
        id: Math.random().toString(36).substr(2, 9),
        url: file,
        status: 'uploading',
        progress: 0,
      }));

      const currentItems = formData.gallery[category] || [];
      const updatedItems = [...currentItems, ...newItems];
      
      updateFormData({
        gallery: {
          ...formData.gallery,
          [category]: updatedItems
        }
      });

      setTimeout(() => {
        newItems.forEach(item => handleUploadItem(item, category));
      }, 0);
    } else if (category === 'menu') {
      const newItems: FormGalleryItem[] = fileArray.map(file => ({
        id: Math.random().toString(36).substr(2, 9),
        url: file,
        status: 'uploading',
        progress: 0,
      }));

      const currentItems = formData.gallery[category] || [];
      const updatedItems = [...currentItems, ...newItems];
      
      updateFormData({
        gallery: {
          ...formData.gallery,
          [category]: updatedItems
        }
      });

      setTimeout(() => {
        newItems.forEach(item => handleUploadItem(item, category));
      }, 0);

    } else {
      // For cover
      const newItem: FormGalleryItem = {
        id: Math.random().toString(36).substr(2, 9),
        url: fileArray[0],
        status: 'uploading',
        progress: 0,
      };
      
      updateFormData({
        gallery: {
          ...formData.gallery,
          [category]: newItem
        }
      });
      
      setTimeout(() => {
        handleUploadItem(newItem, category);
      }, 0);
    }
  }, [formData.gallery, updateFormData, handleUploadItem]);

  const removeFile = (category: 'cover' | 'food' | 'ambiance' | 'menu', index?: number, foodId?: string) => {
    if (category === 'cover') {
      updateFormData({
        gallery: {
          ...formData.gallery,
          [category]: null
        }
      });
    } else if (category === 'menu' && index !== undefined) {
      const currentItems = formData.gallery[category] || [];
      const newItems = currentItems.filter((_, i) => i !== index);
      updateFormData({
        gallery: {
          ...formData.gallery,
          [category]: newItems
        }
      });
    } else if (category === 'food' && foodId) {
      const newFood = (formData.gallery.food || []).filter(item => item.id !== foodId);
      updateFormData({
        gallery: {
          ...formData.gallery,
          food: newFood
        }
      });
    } else if (category === 'ambiance' && index !== undefined) {
      const currentItems = formData.gallery[category] || [];
      const newItems = [...currentItems];
      newItems.splice(index, 1);
      updateFormData({
        gallery: {
          ...formData.gallery,
          [category]: newItems
        }
      });
    }
  };

  const updateFoodName = (foodId: string, name: string) => {
    const updatedFood = (formData.gallery.food || []).map(item =>
      item.id === foodId ? { ...item, label: name } : item
    );
    updateFormData({
      gallery: {
        ...formData.gallery,
        food: updatedFood
      }
    });
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedItem(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetId) return;

    const foodItems = [...(formData.gallery.food || [])];
    const draggedIndex = foodItems.findIndex(item => item.id === draggedItem);
    const targetIndex = foodItems.findIndex(item => item.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const [movedItem] = foodItems.splice(draggedIndex, 1);
    foodItems.splice(targetIndex, 0, movedItem);

    updateFormData({
      gallery: {
        ...formData.gallery,
        food: foodItems
      }
    });
    setDraggedItem(null);
  };

  const renderUploadArea = (
    category: 'cover' | 'food' | 'ambiance' | 'menu',
    multiple: boolean = false,
    compact: boolean = false
  ) => {
    if (compact) {
      return (
        <button
          type="button"
          onClick={() => document.getElementById(`${category}-file`)?.click()}
          className="flex items-center gap-2 px-3 py-2 bg-[#FFD166] text-[#111827] rounded-lg hover:bg-[#E1AD01] transition-colors text-sm font-medium"
        >
          <FaPlus className="text-sm" />
          Add More
          <input
            type="file"
            id={`${category}-file`}
            accept='.jpg,.jpeg,.png,.webp,.heic,.heif'
            multiple={multiple}
            onChange={(e) => e.target.files && handleFileUpload(category, e.target.files, multiple)}
            className="hidden"
          />
        </button>
      );
    }

    return (
      <div
        className="border-2 border-dashed border-[#FFDDA6] rounded-lg p-6 text-center cursor-pointer hover:border-[#FFD166] hover:bg-[#FFF8E7] transition-all group h-full flex flex-col justify-center"
        onClick={() => document.getElementById(`${category}-file`)?.click()}
      >
        <div className="text-3xl text-[#FFD166] mb-3 group-hover:scale-110 transition-transform">
          <FaCloudUploadAlt />
        </div>
        <p className="text-[#5E503F] font-medium mb-2">
          Click or drag to upload {multiple ? 'photos' : 'photo'}
        </p>
        <p className="text-sm text-[#5E503F]">
          Supports JPG, PNG, WEBP
        </p>
        <input
          type="file"
          id={`${category}-file`}
          accept=".jpg,.jpeg,.png,.webp,.heic,.heif"
          multiple={multiple}
          onChange={(e) => e.target.files && handleFileUpload(category, e.target.files, multiple)}
          className="hidden"
        />
      </div>
    );
  };

  const getPreviewSrc = (input?: File | string | null): string | undefined => {
    if (!input) return undefined;
    if (typeof input === "string") return input;
    if (input instanceof File) return URL.createObjectURL(input);
    return undefined;
  };

  const renderStatusOverlay = (item: GalleryItem | FoodItem, category: 'cover' | 'menu' | 'food' | 'ambiance') => {
    const isUploading = uploadingItems.has(item.id);
    const isError = item.status === 'error';
    const isUploaded = item.status === 'uploaded';

    if (isUploading) {
      return (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-white text-center">
            <CircularProgressBar />
          </div>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-white text-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                retryUpload(item, category);
              }}
              className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors shadow-lg"
              title="Retry upload"
            >
              <FaRedo className="text-sm" />
            </button>
          </div>
        </div>
      );
    }

    if (isUploaded) {
      return (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-white text-center">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white shadow-lg">
              <FaCheck className="text-sm" />
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderSinglePhotoSection = (
    title: string,
    description: string,
    category: 'cover',
    icon: JSX.Element
  ) => {
    const files = formData.gallery[category];
    const fileArray = files ? [files] : [];
    const hasPhoto = fileArray.length > 0;

    const item = fileArray[0] as GalleryItem | undefined;
    const src = getPreviewSrc(item?.url);

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-[#111827] flex items-center gap-2 text-sm">
              {icon} {title}
            </h4>
            <p className="text-xs text-[#5E503F]">{description}</p>
          </div>
          {hasPhoto && renderUploadArea(category, false, true)}
        </div>

        {!hasPhoto ? (
          renderUploadArea(category, false, false)
        ) : (
          <div className="relative group">
            <div className="relative w-full h-40 rounded-lg overflow-hidden">
              <img
                src={src}
                alt={`${title} preview`}
                className="w-full h-full object-cover"
              />
              
              {item && renderStatusOverlay(item, category)}
              
              <button
                onClick={() => removeFile(category)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity z-10"
              >
                <FaTimes />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderGallerySection = (
    title: string,
    description: string,
    category: 'food' | 'ambiance' | 'menu',
    multiple: boolean = false,
    icon: JSX.Element
  ) => {
    const items = formData.gallery[category] || [];
    const hasPhotos = items.length > 0;

    return (
      <div className={`space-y-4 ${isActive ? 'fade-in' : 'opacity-0'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-[#111827] flex items-center gap-2">
              {icon} {title}
            </h3>
            <p className="text-sm text-[#5E503F]">{description}</p>
          </div>
          
          {hasPhotos && renderUploadArea(category, multiple, true)}
        </div>

        {!hasPhotos ? (
          renderUploadArea(category, multiple, false)
        ) : (
          <div className={`space-y-4 ${category === 'food' ? '' : 'mt-4'}`}>
            {category === 'food' && items.length > 0 && (
              <div className="space-y-3">
                {(items as FoodItem[]).map((foodItem, index) => {
                  const src = getPreviewSrc(foodItem.url);

                  return (
                    <div
                      key={foodItem.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, foodItem.id)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, foodItem.id)}
                      className={`flex items-start gap-3 p-3 border border-[#FFDDA6] rounded-lg bg-white group cursor-move transition-all ${
                        draggedItem === foodItem.id ? 'opacity-50' : 'hover:border-[#FFD166]'
                      }`}
                    >
                      <div className="flex-shrink-0 text-[#5E503F] mt-2 cursor-grab active:cursor-grabbing">
                        <FaGripVertical />
                      </div>

                      <div className="flex-shrink-0 relative">
                        <div className="relative w-20 h-20 rounded-lg overflow-hidden">
                          {src ? (
                            <img
                              src={src}
                              alt="Food preview"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-200 flex items-center justify-center rounded-lg">
                              <FaImage className="text-gray-400" />
                            </div>
                          )}

                          {renderStatusOverlay(foodItem, 'food')}
                        </div>

                        <button
                          onClick={() => removeFile('food', undefined, foodItem.id)}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        >
                          <FaTimes />
                        </button>
                      </div>

                      <div className="flex-1 min-w-0">
                        <label className="block text-sm font-medium text-[#111827] mb-1">
                          Food Name/Type
                        </label>
                        <input
                          type="text"
                          value={foodItem.label}
                          onChange={(e) => updateFoodName(foodItem.id, e.target.value)}
                          placeholder="e.g., Margherita Pizza, Beef Burger..."
                          className="w-full px-3 py-2 border border-[#FFDDA6] rounded-lg focus:ring-2 focus:ring-[#FFD166] focus:border-transparent text-sm"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {category === 'ambiance' && items.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {(items as GalleryItem[]).map((file, index) => {
                  const src = getPreviewSrc(file.url);
                  
                  return (
                    <div key={file.id} className="relative group">
                      <div className="relative w-full h-40 rounded-lg overflow-hidden">
                        {src ? (
                          <img
                            src={src}
                            alt={`Ambiance ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center rounded-lg">
                            <FaImage className="text-gray-400 text-2xl" />
                          </div>
                        )}

                        {renderStatusOverlay(file, 'ambiance')}

                        <button
                          onClick={() => removeFile(category, index)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {category === 'menu' && items.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {(items as GalleryItem[]).map((file, index) => {
                  const src = getPreviewSrc(file.url);
                  
                  return (
                    <div key={file.id} className="relative group">
                      <div className="relative w-full h-40 rounded-lg overflow-hidden">
                        {src ? (
                          <img
                            src={src}
                            alt={`menu ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center rounded-lg">
                            <FaImage className="text-gray-400 text-2xl" />
                          </div>
                        )}

                        {renderStatusOverlay(file, 'menu')}

                        <button
                          onClick={() => removeFile(category, index)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${isActive ? 'fade-in stagger-2' : 'opacity-0'}`}>
          <div className="space-y-3">
            {renderSinglePhotoSection(
              'Cover Photo',
              'Main image representing your restaurant',
              'cover',
              <FaImage className="text-[#FFD166]" />
            )}
          </div>
        </div>

        <div className={`pt-4 border-t border-[#FFDDA6] ${isActive ? 'fade-in stagger-4' : 'opacity-0'}`}>
          {renderGallerySection(
            'Menu Photos',
            'Show the menu your restaurant',
            'menu',
            true,
            <FaFileAlt className="text-[#FFD166]" />
          )}
        </div>

        <div className={`pt-4 border-t border-[#FFDDA6] ${isActive ? 'fade-in stagger-3' : 'opacity-0'}`}>
          {renderGallerySection(
            'Food Photos',
            'Showcase your delicious dishes. Add names to help guests identify them.',
            'food',
            true,
            <FaUtensils className="text-[#FFD166]" />
          )}
        </div>

        <div className={`pt-4 border-t border-[#FFDDA6] ${isActive ? 'fade-in stagger-4' : 'opacity-0'}`}>
          {renderGallerySection(
            'Ambiance Photos',
            'Show the atmosphere and interior of your restaurant',
            'ambiance',
            true,
            <FaStore className="text-[#FFD166]" />
          )}
        </div>
      </div>
    </div>
  );
};

export default GalleryStep;